import type { CrmMessages } from "./englishCrmMessages";

export const frenchCrmMessages = {
  resources: {
    companies: {
      name: "Entreprise |||| Entreprises",
      forcedCaseName: "Entreprise",
      fields: {
        name: "Nom de l'entreprise",
        website: "Site web",
        linkedin_url: "LinkedIn",
        phone_number: "Numéro de téléphone",
        created_at: "Date de création",
        nb_contacts: "Nombre de contacts",
        revenue: "Chiffre d'affaires",
        sector: "Secteur",
        size: "Taille",
        tax_identifier: "Identifiant fiscal",
        address: "Adresse",
        city: "Ville",
        zipcode: "Code postal",
        state_abbr: "État",
        country: "Pays",
        description: "Description",
        context_links: "URLs de contexte",
        sales_id: "Responsable de compte",
      },
      empty: {
        description: "Il semble que la liste de vos entreprises soit vide.",
        title: "Aucune entreprise trouvée",
      },
      import: {
        title: "Importer des entreprises",
      },
      field_categories: {
        contact: "Contact",
        additional_info: "Informations supplémentaires",
        address: "Adresse",
        context: "Contexte",
      },
      page: { create: "Nouvelle entreprise" },
      action: {
        create: "Nouvelle entreprise",
        edit: "Modifier l'entreprise",
        new: "Nouvelle entreprise",
        show: "Afficher l'entreprise",
      },
      added_on: "Ajoutée le %{date}",
      followed_by: "Suivie par %{name}",
      followed_by_you: "Suivie par vous",
      no_contacts: "Aucun contact",
      nb_contacts: "%{smart_count} contact |||| %{smart_count} contacts",
      nb_deals: "%{smart_count} affaire |||| %{smart_count} affaires",
      related: {
        contacts: "Contacts",
        deals: "Affaires",
        tasks: "Tâches",
        empty_contacts: "Aucun contact dans cette entreprise pour l'instant",
        empty_deals: "Aucune affaire avec cette entreprise pour l'instant",
        empty_tasks: "Ses contacts n'ont aucune tâche en cours",
        add_contact: "Ajouter un contact",
        add_deal: "Ajouter une affaire",
        show_all: "Tout voir (%{count})",
      },
      sizes: {
        one_employee: "1 employé",
        two_to_nine_employees: "2-9 employés",
        ten_to_forty_nine_employees: "10-49 employés",
        fifty_to_two_hundred_forty_nine_employees: "50-249 employés",
        two_hundred_fifty_or_more_employees: "250 employés ou plus",
      },
      autocomplete: {
        create_error:
          "Une erreur s'est produite lors de la création de l'entreprise",
        create_item: "Créer %{item}",
        create_label: "Commencez à taper pour créer une nouvelle entreprise",
      },
    },
    contacts: {
      name: "Contact |||| Contacts",
      forcedCaseName: "Contact",
      field_categories: {
        background_info: "Informations complémentaires",
        identity: "Identité",
        misc: "Divers",
        personal_info: "Informations personnelles",
        position: "Poste",
      },
      fields: {
        first_name: "Prénom",
        last_name: "Nom",
        last_seen: "Dernière activité",
        title: "Titre",
        company_id: "Entreprise",
        email_jsonb: "Adresses e-mail",
        email: "E-mail",
        phone_jsonb: "Numéros de téléphone",
        phone_number: "Numéro de téléphone",
        linkedin_url: "URL LinkedIn",
        background: "Informations de contexte",
        has_newsletter: "Abonné à la newsletter",
        sales_id: "Responsable de compte",
      },
      page: { create: "Nouveau contact" },
      action: {
        add: "Ajouter un contact",
        add_first: "Ajoutez votre premier contact",
        create: "Nouveau contact",
        edit: "Modifier le contact",
        export_vcard: "Exporter en vCard",
        new: "Nouveau contact",
        show: "Afficher le contact",
      },
      autocomplete: {
        create_item: "Créer %{item}",
        create_label: "Saisissez du texte pour créer un contact",
      },
      background: {
        last_activity_on: "Dernière activité le %{date}",
        added_on: "Ajouté le %{date}",
        followed_by: "Suivi par %{name}",
        followed_by_you: "Suivi par vous",
        status_none: "Aucun",
      },
      position_at: "%{title} chez",
      position_at_company: "%{title} chez %{company}",
      empty: {
        description: "Il semble que votre liste de contacts soit vide.",
        title: "Aucun contact trouvé",
      },
      import: {
        title: "Importer des contacts",
      },
      inputs: {
        genders: {
          male: "Monsieur",
          female: "Madame",
          nonbinary: "Indéterminé",
        },
        personal_info_types: {
          work: "Pro",
          home: "Perso",
          other: "Autre",
        },
      },
      list: {
        error_loading: "Erreur lors du chargement des contacts",
        select_contact: "Sélectionner %{name}",
      },
      bulk_tag: {
        action: "Étiqueter",
        back: "Retour aux étiquettes",
        create_description:
          "Créez une nouvelle étiquette et appliquez-la aux contacts sélectionnés.",
        description:
          "Choisissez une étiquette existante ou créez-en une pour les contacts sélectionnés.",
        empty:
          "Aucune étiquette pour le moment. Créez-en une pour étiqueter les contacts sélectionnés.",
        error: "Impossible d'ajouter l'étiquette aux contacts",
        removed:
          "Étiquette retirée de %{smart_count} contact |||| Étiquette retirée de %{smart_count} contacts",
        success:
          "Étiquette ajoutée à %{smart_count} contact |||| Étiquette ajoutée à %{smart_count} contacts",
        title: "Ajouter une étiquette aux contacts",
      },
      merge: {
        action: "Fusionner avec un autre contact",
        confirm: "Fusionner les contacts",
        current_contact: "Contact actuel (sera supprimé)",
        description: "Fusionnez ce contact avec un autre.",
        error: "Échec de la fusion des contacts",
        merging: "Fusion...",
        no_additional_data: "Aucune donnée supplémentaire à fusionner",
        select_target: "Veuillez sélectionner un contact avec lequel fusionner",
        success: "Contacts fusionnés avec succès",
        target_contact: "Contact cible (sera conservé)",
        title: "Fusionner les contacts",
        warning_description:
          "Toutes les données seront transférées au deuxième contact. Cette action ne peut pas être annulée.",
        warning_title: "Avertissement : opération destructrice",
        what_will_be_merged: "Ce qui sera fusionné :",
        will_reassign_notes:
          "%{smart_count} note sera réassignée |||| %{smart_count} notes seront réassignées",
        will_reassign_tasks:
          "%{smart_count} tâche sera réassignée |||| %{smart_count} tâches seront réassignées",
        will_update_deals:
          "%{smart_count} affaire sera mise à jour |||| %{smart_count} affaires seront mises à jour",
        will_add_emails:
          "%{smart_count} adresse email sera ajoutée |||| %{smart_count} adresses email seront ajoutées",
        will_add_phones:
          "%{smart_count} numéro de téléphone sera ajouté |||| %{smart_count} numéros de téléphone seront ajoutés",
      },
      filters: {
        before_last_month: "Avant le mois dernier",
        before_this_month: "Avant ce mois-ci",
        before_this_week: "Avant cette semaine",
        managed_by_me: "Géré par moi",
        search: "Rechercher nom, entreprise...",
        this_week: "Cette semaine",
        today: "Aujourd'hui",
        tags: "Étiquettes",
        tasks: "Tâches",
      },
      hot: {
        empty_change_status:
          'Changez le statut d\'un contact en ajoutant une note à ce contact et en cliquant sur "afficher les options".',
        empty_hint: 'Les contacts avec un statut "chaud" apparaîtront ici.',
        title: "Contacts chauds",
      },
    },
    deals: {
      name: "Affaire |||| Affaires",
      fields: {
        name: "Nom",
        description: "Description",
        company_id: "Entreprise",
        contact_ids: "Contacts",
        categories: "Catégories",
        amount: "Budget",
        expected_closing_date: "Date de clôture prévue",
        stage: "Étape",
      },
      page: { create: "Nouvelle affaire" },
      action: {
        back_to_deal: "Retour à l'affaire",
        create: "Nouvelle affaire",
        new: "Nouvelle affaire",
      },
      field_categories: {
        misc: "Divers",
      },
      filters: {
        only_mine: "Seulement les affaires que je gère",
      },
      archived: {
        action: "Archiver",
        error: "Erreur : affaire non archivée",
        list_title: "Affaires archivées",
        success: "Affaire archivée",
        title: "Affaire archivée",
        view: "Afficher les affaires archivées",
      },
      inputs: {
        linked_to: "Lié à",
      },
      unarchived: {
        action: "Renvoyer au tableau",
        error: "Erreur : affaire non désarchivée",
        success: "Affaire désarchivée",
      },
      updated: "Affaire mise à jour",
      empty: {
        before_create: "avant de créer une affaire.",
        description: "Il semble que votre liste d'affaires soit vide.",
        title: "Aucune affaire trouvée",
      },
      import: {
        title: "Importer des affaires",
      },
      invalid_date: "Date invalide",
    },
    notes: {
      name: "Note |||| Notes",
      forcedCaseName: "Note",
      fields: {
        status: "Statut",
        date: "Date",
        attachments: "Pièces jointes",
        contact_id: "Contact",
        deal_id: "Affaire",
      },
      page: { create: "Nouvelle note" },
      action: {
        add: "Ajouter une note",
        add_first: "Ajoutez votre première note",
        delete: "Supprimer la note",
        edit: "Modifier la note",
        update: "Mettre à jour la note",
        add_this: "Ajouter cette note",
      },
      sheet: {
        create: "Nouvelle note",
        create_for: "Nouvelle note pour %{name}",
        edit: "Modifier la note",
        edit_for: "Modifier la note pour %{name}",
      },
      deleted: "Note supprimée",
      empty: "Aucune note pour l'instant",
      author_added: "%{name} a ajouté une note",
      you_added: "Vous avez ajouté une note",
      me: "Moi",
      list: {
        error_loading: "Erreur lors du chargement des notes",
      },
      note_for_contact: "Note pour %{name}",
      stepper: {
        hint: "Accédez à une page de contact et ajoutez une note",
      },
      added: "Note ajoutée",
      inputs: {
        add_note: "Ajouter une note",
        options_hint: "(joindre des fichiers ou modifier les détails)",
        show_options: "Afficher les options",
      },
      actions: {
        attach_document: "Joindre un document",
      },
      validation: {
        note_or_attachment_required: "Une note ou une pièce jointe est requise",
      },
    },
    sales: {
      name: "Utilisateur |||| Utilisateurs",
      fields: {
        first_name: "Prénom",
        last_name: "Nom",
        email: "E-mail",
        secondary_email: "E-mail secondaire",
        secondary_emails: "E-mails secondaires",
        administrator: "Admin",
        disabled: "Désactivé",
      },
      create: {
        error:
          "Une erreur s'est produite lors de la création de l'utilisateur.",
        success:
          "Utilisateur créé. Ils recevront prochainement un email pour définir leur mot de passe.",
        title: "Nouvel utilisateur",
      },
      edit: {
        error: "Une erreur s'est produite. Veuillez réessayer.",
        record_not_found: "Enregistrement introuvable",
        success: "Utilisateur mis à jour avec succès",
        title: "Modifier %{name}",
      },
      page: { create: "Nouvel utilisateur" },
      action: {
        new: "Nouvel utilisateur",
      },
    },
    tasks: {
      name: "Tâche |||| Tâches",
      forcedCaseName: "Tâche",
      fields: {
        text: "Description",
        due_date: "Date d'échéance",
        type: "Type",
        contact_id: "Contact",
        due_short: "échéance",
      },
      page: { create: "Nouvelle tâche" },
      action: {
        add: "Ajouter une tâche",
        create: "Nouvelle tâche",
        edit: "Modifier la tâche",
      },
      actions: {
        postpone_next_week: "Reporter à la semaine prochaine",
        postpone_tomorrow: "Reporter à demain",
        title: "Actions de tâche",
      },
      added: "Tâche ajoutée",
      deleted: "Tâche supprimée avec succès",
      dialog: {
        create: "Nouvelle tâche",
        create_for: "Nouvelle tâche pour %{name}",
      },
      sheet: {
        edit: "Modifier la tâche",
        edit_for: "Modifier la tâche pour %{name}",
      },
      empty: "Aucune tâche pour l'instant",
      empty_list_hint: "Les tâches ajoutées à vos contacts apparaîtront ici.",
      filters: {
        later: "Plus tard",
        overdue: "En retard",
        this_week: "Cette semaine",
        today: "Aujourd'hui",
        tomorrow: "Demain",
        with_pending: "Avec des tâches en attente",
      },
      regarding_contact: "(Concernant : %{name})",
      updated: "Tâche mise à jour",
      // ---- internal tasks ----
      kind: {
        nav: "Équipe et clients",
        list_title: "Équipe et clients",
        label: "Type de tâche",
        internal: "Interne",
        client: "Projet / Client",
        client_short: "Client",
        all: "Toutes",
        filter_label: "Filtrer les tâches par type",
        assignee: "Assignée à",
        internal_hint: "Pour organiser l’équipe, sans contact.",
        client_hint: "Liée au contact d’un projet ou d’un client.",
        contact_required:
          "Choisissez un contact pour une tâche projet ou client",
        assignee_required: "Choisissez à qui assigner la tâche",
        assigned_to: "Pour %{name}",
        empty_internal: "Aucune tâche interne en attente.",
        empty_client: "Aucune tâche client en attente.",
      },
      // ---- end internal tasks ----
    },
    tags: {
      name: "Étiquette |||| Étiquettes",
      action: {
        add: "Ajouter une étiquette",
        create: "Nouvelle étiquette",
      },
      dialog: {
        color: "Couleur",
        create_title: "Nouvelle étiquette",
        edit_title: "Modifier l'étiquette",
        name_label: "Nom de l'étiquette",
        name_placeholder: "Saisir le nom de l'étiquette",
      },
      empty: "Aucune étiquette pour le moment.",
    },
    // ---- create-page titles of the hub resources (ra-core reads
    // resources.<name>.page.create); their other keys live in fretum/i18n.ts ----
    team_tasks: { page: { create: "Nouvelle tâche" } },
    events: { page: { create: "Nouvel événement" } },
    delivery_templates: { page: { create: "Nouveau modèle de livraison" } },
    time_entries: { page: { create: "Nouvelle saisie de temps" } },
    styles: { page: { create: "Nouveau style" } },
    wiki_pages: { page: { create: "Nouvelle page" } },
    subscriptions: { page: { create: "Nouveau coût" } },
    quotes: { page: { create: "Nouveau devis" } },
    invoices: { page: { create: "Nouvelle facture" } },
    // ---- end hub create-page titles ----
  },
  crm: {
    action: {
      reset_password: "Réinitialiser le mot de passe",
    },
    auth: {
      first_name: "Prénom",
      last_name: "Nom",
      confirm_password: "Confirmer le mot de passe",
      confirmation_required:
        "Veuillez suivre le lien que nous venons de vous envoyer par email pour confirmer votre compte.",
      recovery_email_sent:
        "Si vous êtes un utilisateur enregistré, vous devriez recevoir prochainement un e-mail de récupération de mot de passe.",
      sign_in_failed: "Échec de la connexion.",
      sign_in_google_workspace: "Connectez-vous avec Google Workplace",
      link_error_details:
        "Les liens d'invitation et de réinitialisation du mot de passe ne sont utilisables qu'une fois et expirent après un certain temps. Demandez une nouvelle invitation à un administrateur, ou un nouveau lien via « Mot de passe oublié ? ».",
      link_expired_title: "Ce lien a expiré",
      link_invalid_title: "Ce lien n'est pas valide",
      request_new_link: "Demander un nouveau lien",
      signup: {
        create_account: "Créer un compte",
        create_first_user:
          "Créez le premier compte utilisateur pour terminer la configuration.",
        creating: "Création...",
        initial_user_created: "Utilisateur initial créé avec succès",
      },
      welcome_title: "Bienvenue sur Avante",
    },
    common: {
      account_manager: "Responsable de compte",
      activity: "Activité",
      added: "ajoutée",
      details: "Détails",
      last_activity_with_date: "dernière activité %{date}",
      load_more: "Charger plus",
      misc: "Divers",
      past: "Passé",
      read_more: "En savoir plus",
      retry: "Réessayer",
      show_less: "Afficher moins",
      task_count: "%{smart_count} tâche |||| %{smart_count} tâches",
      unnamed: "Sans nom",
      copied: "Copié !",
      copy: "Copier",
      close_notification: "Fermer la notification",
      loading: "Chargement...",
      me: "Moi",
    },
    empty_state: {
      title: "Pas encore de %{name}",
      hint: "Ce que vous ajoutez apparaîtra ici.",
      filtered_title: "Aucun résultat",
      filtered_hint: "Pas de %{name} correspondant aux filtres actuels.",
      clear_filters: "Effacer les filtres",
      resources: {
        invoices: {
          title: "Aucune facture pour l'instant",
          hint: "Enregistrez vos factures pour suivre ce qui reste à encaisser et ce qui est encaissé.",
        },
        quotes: {
          title: "Aucun devis pour l'instant",
          hint: "Préparez un devis pour un client et suivez son statut.",
        },
        wiki_pages: {
          title: "Le wiki est vide",
          hint: "Documentez les processus, les guides et le savoir-faire de l'équipe.",
        },
        subscriptions: {
          title: "Aucun coût pour l'instant",
          hint: "Ajoutez abonnements et frais fixes pour voir vos dépenses mensuelles.",
        },
        time_entries: {
          title: "Aucune heure saisie",
          hint: "Saisissez le temps passé sur les tâches et les affaires.",
        },
        team_tasks: {
          title: "Aucune tâche d'équipe",
          hint: "Créez une tâche et assignez-la à un membre de l'équipe.",
        },
        delivery_templates: {
          title: "Aucun modèle de livraison",
          hint: "Un modèle crée d'un coup les jalons d'une affaire.",
        },
        styles: {
          title: "Aucun style pour l'instant",
          hint: "Enregistrez couleurs et polices pour les réutiliser dans vos projets.",
        },
        tasks: {
          title: "Aucune tâche",
          hint: "Les tâches que vous créez apparaîtront ici.",
        },
        sales: {
          title: "Aucun utilisateur",
          hint: "Invitez les membres de votre équipe.",
        },
      },
    },
    style_preview: {
      label: "Aperçu du style",
      button: "Bouton",
    },
    splash: {
      greeting: "Hi, %{name}",
    },
    pwa: {
      update_available: "Une nouvelle version est disponible",
      update: "Mettre à jour",
      later: "Plus tard",
    },
    changelog: {
      title: "Nouveautés",
      subtitle:
        "Les derniers changements d'Avante, du plus récent au plus ancien.",
      change_number: "Changement n° %{number}",
      categories_label: "Catégories",
      categories: {
        new: "Nouveauté",
        mobile: "Mobile",
        desktop: "Bureau",
        fix: "Correction",
      },
    },
    activity: {
      added_company: "%{name} a ajouté l'entreprise",
      you_added_company: "Vous avez ajouté l'entreprise",
      added_contact: "%{name} a ajouté le contact",
      you_added_contact: "Vous avez ajouté le contact",
      added_note: "%{name} a ajouté une note sur",
      you_added_note: "Vous avez ajouté une note sur",
      added_note_about_deal: "%{name} a ajouté une note sur l'affaire",
      you_added_note_about_deal: "Vous avez ajouté une note sur l'affaire",
      added_deal: "%{name} a ajouté l'affaire",
      you_added_deal: "Vous avez ajouté l'affaire",
      at_company: "chez",
      to: "à",
      load_more: "Charger plus d'activité",
    },
    dashboard: {
      latest_activity: "Dernière activité",
      latest_activity_error:
        "Erreur lors du chargement de la dernière activité",
      stepper: {
        install: "Installer Avante",
        progress: "%{step}/3 terminé",
        whats_next: "Et ensuite ?",
      },
    },
    data_import: {
      button: "Importer un fichier CSV",
      complete:
        "Import terminé. %{importCount} enregistrements importés, %{errorCount} erreurs",
      csv_file: "Fichier CSV",
      error:
        "Échec de l'importation de ce fichier. Veuillez vous assurer que vous avez fourni un fichier CSV valide.",
      in_progress: "Import en cours…",
      progress:
        "%{importCount} / %{rowCount} enregistrements importés, avec %{errorCount} erreurs.",
      remaining_time: "Temps restant estimé :",
      resource: "Ressource",
      sample_download: "Télécharger un exemple CSV",
      sample_hint:
        "Voici un exemple de fichier CSV que vous pouvez utiliser comme modèle",
      start: "Démarrer l'import",
      stop: "Arrêter l'importation",
      stopped:
        "Import arrêté. %{importCount} enregistrements importés, %{errorCount} erreurs",
      title: "Importer des données",
    },
    header: {
      import_data: "Importer depuis JSON",
    },
    search: {
      title: "Rechercher",
      placeholder:
        "Rechercher un contact, une entreprise, une affaire, une tâche, une note...",
      hint: "Saisissez au moins %{min} caractères pour lancer la recherche",
      empty: "Aucun résultat",
      error:
        "La recherche est indisponible pour le moment. Veuillez réessayer.",
      untitled_result: "(sans titre)",
      show_more: "Afficher plus",
      cancel: "Annuler",
      clear_query: "Effacer la recherche",
      recent: "Récents",
      clear_recent: "Effacer",
      clear_recent_label: "Effacer les recherches récentes",
      quick_links: "Aller à",
      searching: "Recherche…",
      empty_hint: "Essayez un autre nom, une entreprise ou un mot-clé.",
      keys: { navigate: "naviguer", open: "ouvrir", close: "fermer" },
      groups: {
        contacts: "Contacts",
        companies: "Entreprises",
        deals: "Affaires",
        tasks: "Tâches",
        contact_notes: "Notes de contact",
        deal_notes: "Notes d'affaire",
      },
    },
    image_editor: {
      change: "Changer",
      drop_hint:
        "Déposez un fichier à télécharger ou cliquez pour le sélectionner.",
      editable_content: "Contenu modifiable",
      title: "Télécharger et redimensionner l'image",
      update_image: "Mettre à jour l'image",
    },
    import: {
      action: {
        download_error_report: "Téléchargez le rapport d'erreur",
        import: "Importer",
        import_another: "Importer un autre fichier",
      },
      error: {
        unable: "Impossible d'importer ce fichier.",
      },
      idle: {
        description_1:
          "Vous pouvez importer des ventes, des entreprises, des contacts, des entreprises, des notes et des tâches.",
        description_2:
          "Les données doivent se trouver dans un fichier JSON correspondant à l'exemple suivant :",
      },
      status: {
        all_success: "Tous les enregistrements ont été importés avec succès.",
        complete: "Importation terminée.",
        failed: "Échoué",
        imported: "Importé",
        in_progress: "Import en cours, veuillez ne pas quitter cette page.",
        some_failed: "Certains enregistrements n'ont pas été importés.",
        table_caption: "Statut d'importation",
      },
      title: "Importer depuis JSON",
    },
    settings: {
      about: "À propos",
      companies: {
        sectors: "Secteurs",
      },
      dark_mode_logo: "Logo du mode sombre",
      deals: {
        categories: "Catégories",
        currency: "Devise",
        pipeline_help:
          "Sélectionnez les étapes d'affaire à considérer comme des affaires dans le pipeline.",
        pipeline_statuses: "Statuts des pipelines",
        stages: "Étapes",
      },
      light_mode_logo: "Logo du mode clair",
      notes: {
        statuses: "Statuts",
      },
      reset_defaults: "Réinitialiser aux valeurs par défaut",
      save_error: "Échec de l'enregistrement de la configuration",
      saved: "Configuration enregistrée avec succès",
      saving: "Enregistrement...",
      tasks: {
        types: "Types",
      },
      preferences: "Préférences",
      title: "Paramètres",
      app_title: "Titre de l'application",
      sections: {
        branding: "Image de marque",
      },
      validation: {
        duplicate: "%{display_name} en double : %{items}",
        in_use:
          "Impossible de supprimer %{display_name} encore utilisés par des affaires : %{items}",
        validating: "Validation\u2026",
        entities: {
          categories: "catégories",
          stages: "étapes",
        },
      },
    },
    theme: {
      dark: "Sombre",
      label: "Thème",
      light: "Clair",
      system: "Système",
      current: "Thème : %{theme}",
      switch_to: "Passer à %{theme}",
    },
    language: "Langue",
    mobile: {
      search_placeholder: "Rechercher un projet, une tâche, un événement...",
      status_in_progress: "En cours",
      status_upcoming: "À venir",
      status_done: "Terminée",
      nav_home: "Accueil",
      nav_reports: "Rapports",
      nav_messages: "Messages",
      nav_tasks: "Tâches",
      nav_deals: "Opportunités",
      nav_market: "Marché",
      nav_more: "Plus",
      nav_close: "Fermer",
      back: "Retour",
      menu_title: "Sections",
      create: "Créer",
      agenda: "Agenda",
      all_day: "Toute la journée",
      no_events: "Rien de prévu",
      notifications: "Notifications",
      notifications_empty: "Aucune activité récente",
      open_calendar: "Ouvrir l'agenda",
      reports_title: "Rapports",
      reports_subtitle: "Opportunités ouvertes",
      messages_title: "Messages",
      messages_empty: "Aucune note récente",
      week_previous: "Semaine précédente",
      week_next: "Semaine suivante",
      agenda_view: "Vue de l'agenda",
      agenda_day: "Jour",
      agenda_week: "Semaine",
      agenda_month: "Mois",
      agenda_today: "Aujourd'hui",
      agenda_items: "%{smart_count} élément |||| %{smart_count} éléments",
      day_previous: "Jour précédent",
      day_next: "Jour suivant",
      month_previous: "Mois précédent",
      month_next: "Mois suivant",
      deal_missing: "Cette opportunité n'est pas disponible",
      no_deals: "Aucune opportunité ouverte",
      account: "Compte et réglages",
      account_group: "Compte",
      admin_group: "Administration",
      crm_settings: "Paramètres du CRM",
      team_tasks: "Tâches d'équipe",
      close_settings: "Fermer les réglages",
      close_agenda: "Fermer l'agenda",
      close_notifications: "Fermer les notifications",
      settings_title: "Réglages",
      settings_back: "Retour aux réglages",
      more: {
        title: "Plus",
        label: "Modules",
      },
      quick_actions: "Actions rapides",
      plus: {
        new_task: "Nouvelle tâche",
        new_note: "Nouvelle note",
        go_to: "Aller à",
      },
      all_stages: "Toutes les étapes",
      reports_pipeline: "Pipeline ouvert",
      reports_count:
        "%{smart_count} opportunité |||| %{smart_count} opportunités",
      reports_stage_filter: "Filtrer par étape",
      reports_list_title: "Opportunités",
      progress: "Avancement",
      delivery_progress: "Avancement de la livraison",
      stage_progress: "Avancement selon l'étape",
      kanban_empty_deals: "Aucune opportunité",
      // Audit 2 navigation: quick agenda window, calendar, Más groups.
      calendar: "Calendrier",
      more_groups: {
        crm: "CRM",
        hub: "Hub",
      },
      upcoming: {
        title: "Aujourd'hui et à venir",
        open: "Ouvrir aujourd'hui et à venir",
        close: "Fermer aujourd'hui et à venir",
        see_calendar: "Voir le calendrier",
        today: "Aujourd'hui",
        tomorrow: "Demain",
        empty: "Rien de prévu aujourd'hui ni les prochains jours",
        today_empty: "Plus rien aujourd'hui",
      },
    },
    navigation: {
      label: "Navigation CRM",
    },
    profile: {
      add_secondary_email: "Ajouter un e-mail",
      email_taken: "%{email} est déjà utilisée par un autre utilisateur",
      no_secondary_emails: "Aucun",
      secondary_email_invalid: "%{email} n'est pas une adresse e-mail valide",
      secondary_email_is_primary: "%{email} est déjà votre adresse principale",
      secondary_email_taken:
        "%{email} est déjà utilisée par un autre utilisateur",
      too_many_secondary_emails:
        "Vous ne pouvez pas ajouter plus de 10 adresses e-mail secondaires",
      secondary_emails_help:
        "Autres adresses depuis lesquelles vous envoyez des e-mails. Laissez-en une vide pour la supprimer.",
      inbound: {
        description:
          "Vous pouvez commencer à envoyer des e-mails vers l'adresse de réception de votre serveur, par exemple en l'ajoutant au champ %{field}. Avante traitera les e-mails et ajoutera des notes aux contacts correspondants.",
        title: "E-mail entrant",
      },
      mcp: {
        title: "Serveur MCP",
        description:
          "Utilisez cette URL pour connecter votre assistant IA aux données de votre CRM via le Model Context Protocol (MCP).",
        copy: "Copier l'URL du MCP",
        copied: "URL du MCP copiée",
        copy_error: "Impossible de copier l'URL du MCP",
      },
      password: {
        change: "Changer le mot de passe",
      },
      password_reset_sent:
        "Un e-mail de réinitialisation du mot de passe a été envoyé à votre adresse e-mail",
      record_not_found: "Enregistrement introuvable",
      title: "Profil",
      updated: "Votre profil a été mis à jour",
      update_error: "Une erreur s'est produite. Veuillez réessayer",
    },
    validation: {
      invalid_url: "Doit être une URL valide",
      invalid_linkedin_url: "L'URL doit provenir de linkedin.com",
    },
  },
  // ---- desk (desktop redesign): one sub-object per area ----
  desk: {
    // ---- desk.shell (sidebar, sheet header) ----
    shell: {
      nav: {
        all: "Tous",
        all_f: "Toutes",
        pipeline: "Pipeline",
        won: "Gagnées",
        lost: "Perdues",
        pending: "En cours",
        done: "Terminées",
        agenda: "Agenda",
      },
      collapse: "Réduire la barre latérale",
      expand: "Déplier la barre latérale",
      search_placeholder: "Rechercher contacts, sociétés, opportunités…",
      agenda_today:
        "Agenda : %{smart_count} élément aujourd'hui |||| Agenda : %{smart_count} éléments aujourd'hui",
      more_actions: "Plus d'actions",
      refresh: "Actualiser les données",
      dark_mode: "Mode sombre",
      settings: "Paramètres",
      account_menu: "Menu du compte",
      notifications: {
        title: "Alertes",
        overdue: "En retard",
        today: "Aujourd'hui",
        see_all: "Voir les tâches",
        empty: "Rien à faire dans les 7 prochains jours.",
      },
      goal: {
        title: "Clôtures du mois",
        percent_won: "%{percent} % gagné",
        won_prefix: "Vous avez gagné",
        of: "sur",
        expected_suffix: "prévus ce mois-ci.",
        empty: "Aucune opportunité ne doit se conclure ce mois-ci.",
        cta: "Voir le pipeline",
      },
    },
    // ---- end desk.shell ----
    // ---- desk.home (vues d'accueil A « Aujourd'hui » / B « Pouls », bureau et mobile) ----
    home: {
      view_label: "Vue d'accueil",
      views: { today: "Aujourd'hui", pulse: "Pouls" },
      more_title: "Équipe et livraisons",
      summary: {
        attention:
          "%{smart_count} élément demande votre attention |||| %{smart_count} éléments demandent votre attention",
        overdue:
          "%{smart_count} est en retard |||| %{smart_count} sont en retard",
        all_clear: "tout est à jour",
      },
      counters: {
        label: "Aujourd'hui en bref",
        overdue: "En retard",
        today: "Aujourd'hui",
        no_next_step: "Sans prochaine étape",
        no_next_step_short: "Sans étape",
        collect: "À encaisser",
        items: "élément |||| éléments",
        deals: "opport. |||| opport.",
        oldest:
          "la plus ancienne il y a %{smart_count} jour |||| la plus ancienne il y a %{smart_count} jours",
        overdue_none: "rien en retard",
        today_none: "rien de prévu",
        no_next_step_hint: "%{amount} sans tâche ni réunion",
        no_next_step_none: "toutes ont un plan",
        collect_overdue: "%{amount} en retard",
        collect_none: "rien en retard",
        parts: {
          events: "%{smart_count} événement |||| %{smart_count} événements",
          tasks: "%{smart_count} tâche |||| %{smart_count} tâches",
          milestones: "%{smart_count} jalon |||| %{smart_count} jalons",
        },
      },
      inbox: {
        title: "Demande votre attention",
        subtitle: "trié par urgence",
        empty: "Rien en attente. Beau travail.",
        show_more: "Voir %{smart_count} de plus",
        show_less: "Voir moins",
        done_notice: "Terminé : %{title}",
        done_error: "Mise à jour impossible, réessayez",
        kind_internal: "tâche interne",
        kind_client: "tâche client",
        team_task: "tâche d'équipe",
        milestone: "jalon",
        priority_high: "priorité haute",
        priority_urgent: "urgent",
        event_kinds: {
          meeting: "réunion",
          demo: "démo",
          call: "appel",
          internal: "interne",
          deadline: "échéance",
          other: "événement",
        },
        closing_passed: "clôture prévue le %{date} dépassée",
        idle: "sans activité depuis %{smart_count} jour |||| sans activité depuis %{smart_count} jours",
        invoice_overdue:
          "en retard de %{smart_count} jour |||| en retard de %{smart_count} jours",
        invoice_due: "échéance le %{date}",
        groups: {
          overdue: "En retard",
          today: "Aujourd'hui",
          no_next_step: "Opportunités sans prochaine étape",
          collect: "Encaissements",
        },
        hints: {
          oldest:
            "la plus ancienne il y a %{smart_count} jour |||| la plus ancienne il y a %{smart_count} jours",
          at_stake: "%{amount} en jeu",
          pending: "%{amount} en attente",
        },
        chip: {
          yesterday: "hier",
          late: "il y a %{smart_count} jour |||| il y a %{smart_count} jours",
          today: "aujourd'hui",
          closing_late: "+%{smart_count} j",
          idle_days: "%{smart_count} j",
          invoice_late: "retard %{smart_count} j",
        },
        actions: {
          done: "Fait",
          done_label: "Marquer « %{title} » comme fait",
          next_step: "Prochaine étape",
          next_step_short: "+ étape",
          next_step_label: "Planifier la prochaine étape de « %{title} »",
          move_date: "Changer la date",
          open_invoice: "Voir la facture",
          open: "Ouvrir",
        },
      },
      agenda: {
        title: "Agenda du jour",
        week: "Semaine",
        all_day: "Toute la journée",
        more_all_day: "+%{smart_count} de plus sur la journée",
        empty: "Rien d'horodaté aujourd'hui",
        now: "Maintenant",
        now_label: "Maintenant, %{time}",
        tomorrow:
          "Demain : %{smart_count} élément |||| Demain : %{smart_count} éléments",
        see_all: "Voir l'agenda",
        sources: {
          task: "Tâche",
          event: "Événement",
          team_task: "Tâche d'équipe",
          deal_closing: "Clôture prévue",
          milestone: "Jalon",
          renewal: "Renouvellement",
          invoice_due: "Échéance de facture",
        },
      },
      collect: {
        title: "À encaisser",
        vat: "TTC",
        collected_month: "+%{amount} encaissés ce mois-ci",
        overdue_badge: "%{amount} en retard",
        overdue: "En retard",
        dueThisWeek: "Échéance cette semaine",
        later: "Plus tard",
        empty: "Rien à encaisser",
        see_all: "Factures",
      },
      quick: {
        title: "Actions rapides",
        task: "Tâche",
        note: "Note",
        deal: "Opport.",
        invoice: "Facture",
        contact: "Contact",
        task_hint: "Nouvelle tâche",
        note_hint: "Nouvelle note",
        deal_hint: "Nouvelle opportunité",
        invoice_hint: "Nouvelle facture",
        contact_hint: "Nouveau contact",
      },
      next: {
        empty: "Rien d'autre d'horodaté cette semaine",
        now: "maintenant",
        in_minutes: "dans %{smart_count} min",
        today: "Aujourd'hui",
        tomorrow: "Demain",
      },
      mobile: {
        next: "Ensuite",
        agenda: "Agenda",
        attention: "Demande votre attention",
        see_all: "Tout voir",
        collect: "À encaisser",
        invoices: "Factures",
      },
      pulse: {
        title: "Pouls de l'activité",
        subtitle: "en direct depuis vos données",
        approx: "approx.",
        kpi: {
          label: "Chiffres clés",
          pipeline: "Pipeline ouvert",
          pipeline_hint:
            "%{smart_count} opportunité active |||| %{smart_count} opportunités actives",
          won: "Gagné ce mois-ci",
          won_approx:
            "Avante n'enregistre pas la date à laquelle une opportunité est gagnée : on compte les opportunités gagnées dont la clôture prévue tombe ce mois-ci. « Clôtures prévues » n'est pas un objectif : c'est la somme des opportunités qui devraient se conclure ce mois-ci.",
          won_hint: "sur %{amount} de clôtures prévues",
          won_none: "aucune clôture prévue ce mois-ci",
          won_ratio: "%{percent} des clôtures prévues",
          invoiced: "Facturé",
          net: "HT",
          invoiced_hint:
            "%{month} · %{smart_count} facture |||| %{month} · %{smart_count} factures",
          invoiced_trend: "Facturé sur les 6 derniers mois",
          outstanding: "À encaisser",
          outstanding_overdue: "%{amount} en retard",
          outstanding_hint:
            "%{smart_count} facture émise · TTC |||| %{smart_count} factures émises · TTC",
        },
        revenue: {
          title: "Revenus",
          subtitle: "facturé vs encaissé, HT",
          legend: "Séries",
          invoiced: "Facturé",
          collected: "Encaissé",
          total_invoiced: "facturés en 6 mois",
          total_collected: "encaissés en 6 mois",
          empty: "Aucune facture sur les 6 derniers mois",
          month_label: "%{month} : facturé %{invoiced}, encaissé %{collected}",
          month: "Mois",
        },
        funnel: {
          title: "Entonnoir par étape",
          subtitle: "opportunités ouvertes",
          empty: "Aucune opportunité ouverte",
          deals: "%{smart_count} opport. |||| %{smart_count} opport.",
          overdue_closings:
            "Clôture prévue dépassée (%{smart_count}) |||| Clôture prévue dépassée (%{smart_count})",
        },
        deliveries: {
          title: "Projets en livraison",
          active: "%{smart_count} actif |||| %{smart_count} actifs",
          see_all: "Voir les opportunités",
          empty: "Aucun projet en livraison",
          project: "Projet",
          progress: "Avancement",
          status: "Statut",
          next: "Prochain jalon",
          milestones: "jalon %{done} sur %{total}",
          today: "aujourd'hui",
        },
        hours: {
          title: "Heures · %{month}",
          billable: "%{percent} facturables",
          empty: "Aucune heure saisie ce mois-ci",
          logged: "saisies ce mois-ci",
          no_deal: "Interne · sans opportunité",
        },
        costs: {
          title: "Coûts fixes",
          subtitle: "abonnements",
          active: "%{smart_count} actif |||| %{smart_count} actifs",
          empty: "Aucun abonnement actif",
          per_month: "/ mois · %{yearly} / an",
          renewals: "Prochains renouvellements",
          periods: {
            monthly: "mensuel",
            yearly: "annuel",
            one_off: "ponctuel",
          },
          see_all: "Voir les coûts",
        },
      },
      setting: {
        section: "Préférences",
        title: "Vue d'accueil par défaut",
        hint: "Ce que vous voyez en ouvrant Avante sur ordinateur. Enregistré dans ce navigateur.",
      },
    },
    // ---- end desk.home ----
  },
  // ---- end desk ----
} satisfies CrmMessages;
